import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Shell from "@/components/Shell";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Loading } from "@/components/common";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import Students from "@/pages/Students";
import StudentDetail from "@/pages/StudentDetail";
import Prospects from "@/pages/Prospects";
import CallingQueue from "@/pages/CallingQueue";
import CallingCampaigns from "@/pages/CallingCampaigns";
import Followups from "@/pages/Followups";
import Counselling from "@/pages/Counselling";
import Applications from "@/pages/Applications";
import Admissions from "@/pages/Admissions";
import Colleges from "@/pages/Colleges";
import Courses, { CoursePage } from "@/pages/Courses";
import WhatsApp from "@/pages/WhatsApp";
import Calls from "@/pages/Calls";
import Tasks from "@/pages/Tasks";
import Campaigns from "@/pages/Campaigns";
import Payments from "@/pages/Payments";
import ConsultationCharges from "@/pages/ConsultationCharges";
import Incentives from "@/pages/Incentives";
import Reports from "@/pages/Reports";
import Automation from "@/pages/Automation";
import FlowchartBuilder from "@/pages/FlowchartBuilder";
import Integrations from "@/pages/Integrations";
import FacebookIntegration from "@/pages/FacebookIntegration";
import WebsiteLeads from "@/pages/WebsiteLeads";
import CampaignDashboard from "@/pages/CampaignDashboard";
import ImportExport from "@/pages/ImportExport";
import AuditLog from "@/pages/AuditLog";
import Settings from "@/pages/Settings";
import Team from "@/pages/Team";

function AdminOnly({ children }) {
  const { user } = useAuth();
  return ["super_admin", "admin"].includes(user?.role) ? children : <Navigate to="/" replace />;
}
import CollegeDetail from "@/pages/CollegeDetail";
import Scholarships from "@/pages/Scholarships";
import Performance from "@/pages/Performance";
import Faqs from "@/pages/Faqs";
import Cutoffs from "@/pages/Cutoffs";
import Chat from "@/pages/Chat";
import Attendance from "@/pages/Attendance";
import AdminTools from "@/pages/AdminTools";
import LeadFields from "@/pages/LeadFields";
import MyPhone from "@/pages/MyPhone";

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loading label="Starting OhCampus CRM" />;
  if (user === false) return <Navigate to="/login" replace />;
  return <Shell><ErrorBoundary routeKey={location.pathname}>{children}</ErrorBoundary></Shell>;
}

function LoginGate() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

const P = (el) => <Protected>{el}</Protected>;

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginGate />} />
            <Route path="/" element={P(<Dashboard />)} />
            <Route path="/leads" element={P(<Leads />)} />
            <Route path="/students" element={P(<Students />)} />
            <Route path="/students/:id" element={P(<StudentDetail />)} />
            <Route path="/prospects" element={P(<Prospects />)} />
            <Route path="/calling-queue" element={P(<CallingQueue />)} />
            <Route path="/calling-campaigns" element={P(<CallingCampaigns />)} />
            <Route path="/calling-campaigns/:id" element={P(<CampaignDashboard />)} />
            <Route path="/integrations" element={P(<Integrations />)} />
            <Route path="/integrations/facebook" element={P(<FacebookIntegration />)} />
            <Route path="/integrations/website" element={P(<WebsiteLeads />)} />
            <Route path="/followups" element={P(<Followups />)} />
            <Route path="/counselling" element={P(<Counselling />)} />
            <Route path="/applications" element={P(<Applications />)} />
            <Route path="/admissions" element={P(<Admissions />)} />
            <Route path="/colleges" element={P(<Colleges />)} />
            <Route path="/courses/:id" element={P(<CoursePage />)} />
            <Route path="/colleges/:id" element={P(<CollegeDetail />)} />
            <Route path="/scholarships" element={P(<Scholarships />)} />
            <Route path="/performance" element={P(<Performance />)} />
            <Route path="/faqs" element={P(<Faqs />)} />
            <Route path="/cutoffs" element={P(<Cutoffs />)} />
            <Route path="/chat" element={P(<Chat />)} />
            <Route path="/attendance" element={P(<Attendance />)} />
            <Route path="/admin-tools" element={P(<AdminOnly><AdminTools /></AdminOnly>)} />
            <Route path="/courses" element={P(<Courses />)} />
            <Route path="/whatsapp" element={P(<WhatsApp />)} />
            <Route path="/calls" element={P(<Calls />)} />
            <Route path="/tasks" element={P(<Tasks />)} />
            <Route path="/campaigns" element={P(<Campaigns />)} />
            <Route path="/payments" element={P(<Payments />)} />
            <Route path="/consultation-charges" element={P(<AdminOnly><ConsultationCharges /></AdminOnly>)} />
            <Route path="/incentives" element={P(<Incentives />)} />
            <Route path="/reports" element={P(<Reports />)} />
            <Route path="/automation" element={P(<Automation />)} />
            <Route path="/flow-builder" element={P(<FlowchartBuilder />)} />
            <Route path="/flow-builder/:id" element={P(<FlowchartBuilder />)} />
            <Route path="/import" element={P(<ImportExport />)} />
            <Route path="/audit" element={P(<AuditLog />)} />
            <Route path="/team" element={P(<Team />)} />
            <Route path="/settings" element={P(<AdminOnly><Settings /></AdminOnly>)} />
            <Route path="/settings/lead-fields" element={P(<AdminOnly><LeadFields /></AdminOnly>)} />
            <Route path="/my-phone" element={P(<MyPhone />)} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
